/**
 * Wizard State Management Utility
 * Handles state persistence, navigation, and unsaved changes detection
 */

import React from 'react';

/**
 * Wizard state storage keys
 */
const STORAGE_KEYS = {
  WIZARD_STATE: 'parrot_wizard_state',
  UNSAVED_CHANGES: 'parrot_wizard_unsaved_changes',
  NAVIGATION_INTENT: 'parrot_wizard_navigation_intent'
};

/**
 * Wizard state manager class
 */
export class WizardStateManager {
  constructor(sessionId = null, parrotId = null) {
    this.sessionId = sessionId;
    this.parrotId = parrotId;
    this.storageKey = this.getStorageKey();
    this.changeListeners = new Set();
    this.navigationListeners = new Set();
  }

  /**
   * Get unique storage key for this wizard session
   */
  getStorageKey() {
    const identifier = this.parrotId || this.sessionId || 'new';
    return `${STORAGE_KEYS.WIZARD_STATE}_${identifier}`;
  }

  /**
   * Save wizard state to localStorage
   */
  saveState(step, data, options = {}) {
    try {
      const currentState = this.getState() || {};
      const timestamp = new Date().toISOString();
      
      const updatedState = {
        ...currentState,
        sessionId: this.sessionId,
        parrotId: this.parrotId,
        currentStep: step,
        lastSaved: timestamp,
        [`step${step}Data`]: {
          ...data,
          savedAt: timestamp
        },
        [`step${step}Completed`]: options.completed || false,
        hasUnsavedChanges: !options.completed
      };

      localStorage.setItem(this.storageKey, JSON.stringify(updatedState));
      
      // Notify listeners
      this.notifyChangeListeners('state_saved', { step, data, state: updatedState });
      
      return updatedState;
    } catch (error) {
      console.error('Failed to save wizard state:', error);
      return null;
    }
  }

  /**
   * Get current wizard state
   */
  getState() {
    try {
      const stateJson = localStorage.getItem(this.storageKey);
      return stateJson ? JSON.parse(stateJson) : null;
    } catch (error) {
      console.error('Failed to get wizard state:', error);
      return null;
    }
  }

  /**
   * Get data for a specific step
   */
  getStepData(step) {
    const state = this.getState();
    return state ? state[`step${step}Data`] : null;
  }

  /**
   * Check if a step is completed
   */
  isStepCompleted(step) {
    const state = this.getState();
    return state ? state[`step${step}Completed`] || false : false;
  }

  /**
   * Check if there are unsaved changes
   */
  hasUnsavedChanges() {
    const state = this.getState();
    return state ? state.hasUnsavedChanges || false : false;
  }

  /**
   * Mark step as completed
   */
  markStepCompleted(step, data = null) {
    const currentState = this.getState() || {};
    const updatedState = {
      ...currentState,
      [`step${step}Completed`]: true,
      hasUnsavedChanges: false
    };

    if (data) {
      updatedState[`step${step}Data`] = {
        ...data,
        completedAt: new Date().toISOString()
      };
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(updatedState));
      this.notifyChangeListeners('step_completed', { step, data, state: updatedState });
      return updatedState;
    } catch (error) {
      console.error('Failed to mark step as completed:', error);
      return null;
    }
  }

  /**
   * Clear wizard state
   */
  clearState() {
    try {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(`${STORAGE_KEYS.UNSAVED_CHANGES}_${this.parrotId || this.sessionId || 'new'}`);
      this.notifyChangeListeners('state_cleared', {});
      return true;
    } catch (error) {
      console.error('Failed to clear wizard state:', error);
      return false;
    }
  }

  /**
   * Set navigation intent (for handling unsaved changes)
   */
  setNavigationIntent(targetStep, targetRoute = null) {
    const intent = {
      targetStep,
      targetRoute,
      timestamp: new Date().toISOString(),
      hasUnsavedChanges: this.hasUnsavedChanges()
    };

    try {
      localStorage.setItem(
        `${STORAGE_KEYS.NAVIGATION_INTENT}_${this.parrotId || this.sessionId || 'new'}`,
        JSON.stringify(intent)
      );
      
      this.notifyNavigationListeners('navigation_intent_set', intent);
      return intent;
    } catch (error) {
      console.error('Failed to set navigation intent:', error);
      return null;
    }
  }

  /**
   * Get navigation intent
   */
  getNavigationIntent() {
    try {
      const intentJson = localStorage.getItem(
        `${STORAGE_KEYS.NAVIGATION_INTENT}_${this.parrotId || this.sessionId || 'new'}`
      );
      return intentJson ? JSON.parse(intentJson) : null;
    } catch (error) {
      console.error('Failed to get navigation intent:', error);
      return null;
    }
  }

  /**
   * Clear navigation intent
   */
  clearNavigationIntent() {
    try {
      localStorage.removeItem(
        `${STORAGE_KEYS.NAVIGATION_INTENT}_${this.parrotId || this.sessionId || 'new'}`
      );
      this.notifyNavigationListeners('navigation_intent_cleared', {});
      return true;
    } catch (error) {
      console.error('Failed to clear navigation intent:', error);
      return false;
    }
  }

  /**
   * Check if navigation should be blocked due to unsaved changes
   */
  shouldBlockNavigation(targetStep) {
    const state = this.getState();
    if (!state || !state.hasUnsavedChanges) {
      return false;
    }

    // Don't block navigation to the same step
    if (targetStep === state.currentStep) {
      return false;
    }

    return true;
  }

  /**
   * Get changes summary for confirmation dialog
   */
  getChangesSummary() {
    const state = this.getState();
    if (!state) return null;

    const changes = [];
    
    // Check each step for unsaved data
    for (let step = 1; step <= 3; step++) {
      const stepData = state[`step${step}Data`];
      const stepCompleted = state[`step${step}Completed`];
      
      if (stepData && !stepCompleted) {
        changes.push({
          step,
          stepName: this.getStepName(step),
          hasData: true,
          lastSaved: stepData.savedAt
        });
      }
    }

    return {
      hasChanges: changes.length > 0,
      changes,
      lastActivity: state.lastSaved
    };
  }

  /**
   * Get human-readable step name
   */
  getStepName(step) {
    const stepNames = {
      1: 'Basic Configuration',
      2: 'Voice Settings',
      3: 'AI Behavior'
    };
    return stepNames[step] || `Step ${step}`;
  }

  /**
   * Add change listener
   */
  addChangeListener(listener) {
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  /**
   * Add navigation listener
   */
  addNavigationListener(listener) {
    this.navigationListeners.add(listener);
    return () => this.navigationListeners.delete(listener);
  }

  /**
   * Notify change listeners
   */
  notifyChangeListeners(event, data) {
    this.changeListeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Error in change listener:', error);
      }
    });
  }

  /**
   * Notify navigation listeners
   */
  notifyNavigationListeners(event, data) {
    this.navigationListeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Error in navigation listener:', error);
      }
    });
  }

  /**
   * Validate wizard state integrity
   */
  validateState() {
    const state = this.getState();
    if (!state) return { valid: true, issues: [] };

    const issues = [];

    // Check for required fields in each step
    if (state.step1Data) {
      if (!state.step1Data.name || !state.step1Data.first_message) {
        issues.push('Step 1: Missing required fields (name or first message)');
      }
    }

    if (state.step2Data) {
      if (!state.step2Data.voice_id || !state.step2Data.creativity_level) {
        issues.push('Step 2: Missing required fields (voice or creativity level)');
      }
    }

    // Check for data consistency
    if (state.sessionId && this.sessionId && state.sessionId !== this.sessionId) {
      issues.push('Session ID mismatch detected');
    }

    if (state.parrotId && this.parrotId && state.parrotId !== this.parrotId) {
      issues.push('Parrot ID mismatch detected');
    }

    return {
      valid: issues.length === 0,
      issues
    };
  }

  /**
   * Merge state from another source (e.g., API response)
   */
  mergeState(externalState, options = {}) {
    const currentState = this.getState() || {};
    const { overwrite = false, preserveUnsaved = true } = options;

    let mergedState;
    
    if (overwrite) {
      mergedState = {
        ...externalState,
        sessionId: this.sessionId,
        parrotId: this.parrotId,
        mergedAt: new Date().toISOString()
      };
    } else {
      mergedState = {
        ...externalState,
        ...currentState,
        sessionId: this.sessionId,
        parrotId: this.parrotId,
        mergedAt: new Date().toISOString()
      };
    }

    // Preserve unsaved changes if requested
    if (preserveUnsaved && currentState.hasUnsavedChanges) {
      mergedState.hasUnsavedChanges = true;
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(mergedState));
      this.notifyChangeListeners('state_merged', { 
        external: externalState, 
        current: currentState, 
        merged: mergedState 
      });
      return mergedState;
    } catch (error) {
      console.error('Failed to merge wizard state:', error);
      return null;
    }
  }

  /**
   * Export state for debugging or backup
   */
  exportState() {
    const state = this.getState();
    const intent = this.getNavigationIntent();
    
    return {
      state,
      intent,
      metadata: {
        sessionId: this.sessionId,
        parrotId: this.parrotId,
        storageKey: this.storageKey,
        exportedAt: new Date().toISOString()
      }
    };
  }

  /**
   * Import state from backup
   */
  importState(exportedData) {
    try {
      if (exportedData.state) {
        localStorage.setItem(this.storageKey, JSON.stringify(exportedData.state));
      }
      
      if (exportedData.intent) {
        localStorage.setItem(
          `${STORAGE_KEYS.NAVIGATION_INTENT}_${this.parrotId || this.sessionId || 'new'}`,
          JSON.stringify(exportedData.intent)
        );
      }

      this.notifyChangeListeners('state_imported', exportedData);
      return true;
    } catch (error) {
      console.error('Failed to import wizard state:', error);
      return false;
    }
  }
}

/**
 * Create wizard state manager instance
 */
export const createWizardStateManager = (sessionId = null, parrotId = null) => {
  return new WizardStateManager(sessionId, parrotId);
};

/**
 * Hook for using wizard state manager in React components
 */
export const useWizardStateManager = (sessionId = null, parrotId = null) => {
  const [stateManager] = React.useState(() => createWizardStateManager(sessionId, parrotId));
  
  React.useEffect(() => {
    // Update manager IDs if they change
    if (sessionId !== stateManager.sessionId || parrotId !== stateManager.parrotId) {
      stateManager.sessionId = sessionId;
      stateManager.parrotId = parrotId;
      stateManager.storageKey = stateManager.getStorageKey();
    }
  }, [sessionId, parrotId, stateManager]);

  return stateManager;
};

/**
 * Utility functions for wizard state management
 */
export const WizardStateUtils = {
  /**
   * Clean up old wizard states (older than 24 hours)
   */
  cleanupOldStates() {
    try {
      const cutoffTime = Date.now() - (24 * 60 * 60 * 1000); // 24 hours ago
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_KEYS.WIZARD_STATE)) {
          try {
            const stateJson = localStorage.getItem(key);
            const state = JSON.parse(stateJson);
            const lastSaved = new Date(state.lastSaved).getTime();
            
            if (lastSaved < cutoffTime) {
              keysToRemove.push(key);
            }
          } catch (error) {
            // If we can't parse the state, remove it
            keysToRemove.push(key);
          }
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
      return keysToRemove.length;
    } catch (error) {
      console.error('Failed to cleanup old wizard states:', error);
      return 0;
    }
  },

  /**
   * Get all active wizard sessions
   */
  getActiveWizardSessions() {
    try {
      const sessions = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_KEYS.WIZARD_STATE)) {
          try {
            const stateJson = localStorage.getItem(key);
            const state = JSON.parse(stateJson);
            sessions.push({
              key,
              sessionId: state.sessionId,
              parrotId: state.parrotId,
              currentStep: state.currentStep,
              lastSaved: state.lastSaved,
              hasUnsavedChanges: state.hasUnsavedChanges
            });
          } catch (error) {
            console.warn('Failed to parse wizard state:', key, error);
          }
        }
      }

      return sessions;
    } catch (error) {
      console.error('Failed to get active wizard sessions:', error);
      return [];
    }
  }
};
